import { GetCombinedFlowcodeDesignsResponseType } from '@dtx-company/app/shared/code/src/types/flowcode.types'
import { FcGeneratorOptions } from '@dtx-company/flowcode-generator-browser'
import { DEFAULT_FLOWCODE_DESTINATION_LOGGED_IN } from '@dtx-company/app/shared/code/src/constants/flowcode'
import { FlowcodeTemplateType } from '../../../types/flowcode'
import { Venmo } from '@dtx-company/flowcode-utils/src'
import { IthacaOrg } from '@dtx-company/ithaca-sdk/src/auth/types'
import { PermissionName } from '../../../components/FlowTeams/types/asset-permissions.types'
import { Routes } from '@dtx-company/true-common/src/constants/routes'
import { VCardProperties } from '@dtx-company/app/shared/code/src/components/landing-page/destination-form/VCardDestinationForm/types'

export interface AutoDesign {
  color1: string
  color2: string
  color3: string
  logoData: {
    logoHeight: number
    logoWidth: number
    logoImageUrl: string
  }
  id: string
  partialIds: string[]
}
export interface AutoDesignedCode {
  ids: string[]
  configuration: string
}
export interface CustomDesign {
  pattern: string
  singleColor: string
  shape: string
  centerText: string
  logoData: {
    logoHeight: number
    logoWidth: number
    logoImageUrl: string
  }
  rimTopText: string
  rimBottomText: string
  showFrame: boolean
  video: boolean
}
export enum DesignType {
  AUTO = 'auto',
  CUSTOM = 'custom',
  SAVED = 'saved'
}
export enum DestinationType {
  URL = 'URL',
  FILE = 'File',
  SOCIALS = 'Socials',
  FLOWPAGE = 'Flowpage',
  WIFI = 'Wifi',
  SMS = 'SMS',
  CALL = 'Call',
  EMAIL = 'Email',
  VCARD = 'V Card'
}
export enum Social {
  YouTube = 'YouTube',
  Instagram = 'Instagram',
  Facebook = 'Facebook',
  Venmo = 'Venmo',
  LinkedIn = 'LinkedIn',
  TikTok = 'TikTok',
  Snapchat = 'Snapchat',
  Twitter = 'Twitter',
  Spotify = 'Spotify'
}
export interface DestinationValue {
  url?: string
  fileObjectURL?: string
  platform?: Social
  socialURL?: string
  venmo?: Venmo
  flowpage?: string
  SSID?: string
  method?: string
  password?: string
  sms?: string
  smsCC?: string
  smsMessage?: string
  phone?: string
  phoneCC?: string
  email?: string
  emailSubject?: string
  emailMessage?: string
  vcard: VCardProperties
}
export interface CreateFlowcodeState {
  data: {
    templates: GetCombinedFlowcodeDesignsResponseType & {
      customizableTemplateOptions: FlowcodeTemplateType[]
      nonCustomizableTemplateOptions: FlowcodeTemplateType[]
    }
    autoDesignedCodes: AutoDesignedCode[]
    firstAutoDesignedCodes: AutoDesignedCode[]
    org: IthacaOrg | undefined
  }
  defaultActiveConfig: FcGeneratorOptions
  activeConfig: FcGeneratorOptions
  directoryId: string
  redirectTo: string
  // page control
  isCreateOneFlowcode: boolean
  isScannabilityModalOpen: boolean
  didScannabilityCheckPass: boolean | null
  isAttemptingToSubmit: boolean
  postCreateRedirectTo:
    | {
        pathname: Routes
        query: {
          batchId: string
          download: boolean
          savedAsTemplate: boolean
          source?: string
        }
      }
    | undefined
  hasDestinationError: boolean | null
  hasVCardError: Partial<Record<keyof VCardProperties, boolean>>
  isUserAtCodeLimit: boolean
  selectedSavedDesignName: string
  // single create
  flowcodeNickname: string
  // bulk create
  csvUrl: string
  folderName: string
  // destination
  destinationType: DestinationType
  destinationValue: DestinationValue
  // design
  designType: DesignType
  selectedAutoDesign: AutoDesign
  selectedCustomDesign: CustomDesign
  defaultCustomDesign: CustomDesign
  selectedSavedDesignFlowcodeTemplate: string
  selectedSavedDesignBatchTemplate: string
  isUsingVideoOptimization: boolean
  specialConfig: {
    isSinclairOrgUser: boolean
    hidePrivacySmile: boolean
  }
  isUsingFlowcodeBranding: boolean
  // else
  saveDesignAsTemplate: string
  isUsingStaticFlowcode: boolean
  selectedRedirectSubdomain: string
  isUsingAdvancedGeolocation: boolean
  selectedTags: string[]
  selectedMembers: { id: string; email: string; permission: PermissionName }[]
  isSharingWithAllMembers: boolean
}

export const placeholderActiveConfig: FcGeneratorOptions = {
  data: DEFAULT_FLOWCODE_DESTINATION_LOGGED_IN,
  qrrotate: false,
  logoWidth: 0,
  logoHeight: 0,
  radiusMask: 89,
  containerText: [
    {
      font: 'inter_medium',
      text: 'PRIVACY.FLOWCODE.COM',
      fontSize: 9,
      position: {
        property: 7,
        offsetPercentage: 3.1,
        extraDegrees: 0
      },
      fontColor: '#000000'
    }
  ],
  containerShape: 1,
  gridModuleColor: '#000000',
  gridModuleShape: 4,
  gridModuleImageUrl: null,
  containerBorderSize: 8.5,
  gridBackgroundColor: '#ffffff',
  containerBorderColor: '#000000',
  errorCorrectionLevel: 2,
  gridModuleRandomSizes: '100',
  dataGridPercentageSize: 69,
  gridModuleColorFillSvg: false,
  positionElementTopLeft: {
    fillSvg: false,
    innerShape: 2,
    outerShape: 2,
    innerImageUrl: null,
    backgroundColor: '#ffffff',
    innerShapeColor: '#000000',
    innerShapeWidth: 45,
    outerShapeColor: '#000000',
    outerShapeWidth: 15
  },
  containerEmptySpaceSize: 6,
  positionElementTopRight: {
    fillSvg: false,
    innerShape: 2,
    outerShape: 2,
    innerImageUrl: null,
    backgroundColor: '#ffffff',
    innerShapeColor: '#000000',
    innerShapeWidth: 45,
    outerShapeColor: '#000000',
    outerShapeWidth: 15
  },
  containerBackgroundColor: '#ffffff',
  containerImageUrlOpacity: 1,
  positionElementBottomLeft: {
    text: {
      font: 'inter_black',
      text: 'FLOWCODE',
      fontSize: 12,
      position: {
        property: 1,
        offsetPercentage: 0,
        extraDegrees: 0
      },
      fontColor: '#000000'
    },
    fillSvg: false,
    innerShape: 2,
    outerShape: 5,
    innerImageUrl: null,
    backgroundColor: '#ffffff',
    innerShapeColor: '#000000',
    innerShapeWidth: 45,
    outerShapeColor: '#000000',
    outerShapeWidth: 15
  },
  containerOuterPatternColor: '#000000',
  containerOuterPatternShape: 4,
  containerImageBackgroundOnly: false,
  containerImageOverlayPrivacy: true,
  containerOuterPatternImageUrl: null,
  containerBackgroundInsetExtend: 0,
  containerOuterPatternRandomSizes: '100',
  containerOuterPatternColorFillSvg: false,
  defaultColor: '#000000',
  alwaysUseDefaultColor: true,
  defaultBackgroundColor: '#ffffff'
}
export const defaultAutoDesign = {
  color1: '#000000',
  color2: '',
  color3: '',
  logoData: {
    logoHeight: 50,
    logoWidth: 50,
    logoImageUrl: ''
  },
  id: '',
  partialIds: []
}
export const placeholderCustomDesign = {
  pattern: '',
  singleColor: '#000000',
  shape: 'circle',
  centerText: '',
  logoData: {
    logoHeight: 50,
    logoWidth: 50,
    logoImageUrl: ''
  },
  rimTopText: '',
  rimBottomText: '',
  video: false,
  showFrame: false
}

export const initialState: CreateFlowcodeState = {
  data: {
    templates: {
      flowcodeTemplates: [],
      batchTemplates: [],
      customAssignedCodes: [],
      customizableTemplateOptions: [],
      nonCustomizableTemplateOptions: []
    },
    autoDesignedCodes: [],
    firstAutoDesignedCodes: [],
    org: undefined
  },
  defaultActiveConfig: placeholderActiveConfig,
  activeConfig: placeholderActiveConfig,
  directoryId: '',
  redirectTo: '',
  // page control
  isCreateOneFlowcode: true,
  isScannabilityModalOpen: false,
  didScannabilityCheckPass: null,
  isAttemptingToSubmit: false,
  postCreateRedirectTo: undefined,
  hasDestinationError: null,
  hasVCardError: {},
  isUserAtCodeLimit: false,
  selectedSavedDesignName: '',
  // single create
  flowcodeNickname: '',
  destinationType: DestinationType.URL,
  destinationValue: { vcard: {} },
  // bulk create
  csvUrl: '',
  folderName: '',
  // design
  designType: DesignType.SAVED,
  selectedAutoDesign: defaultAutoDesign,
  selectedCustomDesign: placeholderCustomDesign,
  defaultCustomDesign: placeholderCustomDesign,
  selectedSavedDesignFlowcodeTemplate: '',
  selectedSavedDesignBatchTemplate: '',
  isUsingVideoOptimization: false,
  saveDesignAsTemplate: '',
  specialConfig: {
    isSinclairOrgUser: false,
    hidePrivacySmile: true
  },
  isUsingFlowcodeBranding: true,
  // else
  isUsingStaticFlowcode: false,
  selectedRedirectSubdomain: '',
  isUsingAdvancedGeolocation: false,
  selectedTags: [],
  selectedMembers: [],
  isSharingWithAllMembers: false
}
